import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Text, Input, Field, Button } from "@fluentui/react-components";
import { Eye24Regular, EyeOff24Regular } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "#root/redux/auth/actions";
import MetaData from "../MetaData";
import "./styles.scss";
import { ROUTES } from "#root/routes/constants";
import { showToast } from "#root/components/GlobalToaster";
import { RootState } from "#store/store";
import { useOrganizationRequest } from "#root/queries/auth";
import { useQueryClient } from "@tanstack/react-query";
import { TenantSelectionDialog } from "#root/components/TenantSelectionDialog";

interface LoginForm {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [loginForm, setLoginForm] = useState<LoginForm>({ email: "", password: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [loginRequested, setLoginRequested] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    data: tenantsData,
    error: tenantsError,
    isLoading: tenantsLoading,
    refetch: fetchOrganizations,
  } = useOrganizationRequest({ email: loginForm.email }, { enabled: false });

  const tenants = tenantsData?.items || [];

  const { isAuthenticated, currentUserLoading, error } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    queryClient.invalidateQueries();
    if (isAuthenticated && !currentUserLoading) {
      navigate(ROUTES.ROOT);
    }
  }, [isAuthenticated, currentUserLoading, navigate]);

  useEffect(() => {
    if (tenantsError || error) {
      setLoginRequested(false);
      setIsSubmitted(false);
      showToast(`${tenantsError?.message || error}`, "error", "top");
    }
  }, [tenantsError, error]);

  const handleLogin = (tenantId?: string) => {
    setLoginRequested(true);
    dispatch(
      loginUser({
        email: loginForm.email,
        password: loginForm.password,
        tenantId,
      })
    );
  };

  useEffect(() => {
    if (!isSubmitted || tenantsLoading || loginRequested || !tenants) return;
    if (tenants.length <= 1) {
      handleLogin(tenants[0]?.id);
    } else {
      setSelectedTenantId(tenants[0]?.id || "");
      setShowTenantModal(true);
    }
  }, [tenants, isSubmitted, tenantsLoading, loginRequested]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginRequested(false);
    setIsSubmitted(true);
    fetchOrganizations();
  };

  const handleTenantSelect = () => {
    if (!selectedTenantId || loginRequested) return;
    setShowTenantModal(false);
    handleLogin(selectedTenantId);
  };

  const closeTenantDialog = () => {
    setShowTenantModal(false);
    setIsSubmitted(false);
  };

  return (
    <>
      <MetaData title="Login" description="Login to your EMC account" />
      <div className="login-page">
        <div className="login-page__left">
          <div className="login-page__form-container">
            <div className="login-page__form-header">
              <img src="/images/emc-logo.svg" alt="EMC Logo" className="login-page__logo" />
            </div>
            <h1 className="login-page__title">Login</h1>
            <form className="auth-form" onSubmit={handleSubmit}>
              <Field label="Email" required>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={loginForm.email}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, email: e.target.value })
                  }
                />
              </Field>
              <Field label="Password" required className="password-field">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, password: e.target.value })
                  }
                  contentAfter={
                    <Text
                      as="span"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                    </Text>
                  }
                />
                <Link to="/forgot-password" className="forgot-password">
                  Forgot your password?
                </Link>
              </Field>
              <div>
                <Button type="submit" appearance="primary" disabled={tenantsLoading || currentUserLoading}>
                  {tenantsLoading || currentUserLoading ? "Please wait..." : "Login"}
                </Button>
              </div>
              <div className="signup-text">
                Don't have an account? <Link to={ROUTES.SIGN_UP}>Sign Up</Link>
              </div>
            </form>
          </div>
          <div className="login-page__footer">
            <p className="login-page__copyright">&copy; {new Date().getFullYear()} EMC</p>
          </div>
        </div>
        <div className="login-page__right" />
        <TenantSelectionDialog
          open={showTenantModal}
          onOpenChange={closeTenantDialog}
          title="Select the Organization"
          primaryButtonText="Login to Organization"
          onPrimaryButtonClick={handleTenantSelect}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={closeTenantDialog}
          isPrimaryButtonLoading={currentUserLoading}
          isPrimaryButtonDisabled={!selectedTenantId || tenantsLoading}
          isSecondaryButtonDisabled={currentUserLoading}
          selectedTenantId={selectedTenantId}
          setSelectedTenantId={setSelectedTenantId}
          tenants={tenants}
        />
      </div>
    </>
  );
};

export default Login;
